import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Optimized LazyLoad component with virtual scrolling
 * - Loads content when entering viewport + offset
 * - Unloads content when far from viewport to reduce DOM nodes
 * - Handles fast scrolling and large lists (3000+ items)
 */
const LazyLoad = ({ children, height, offset = 0, unloadOffset = 0, onContentVisible }) => {
  const [isVisible, setIsVisible] = useState(false);
  const placeholderRef = useRef(null);

  useEffect(() => {
    const placeholder = placeholderRef.current;
    if (!placeholder) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Load content when entering viewport + offset
            setIsVisible((wasVisible) => {
              if (!wasVisible && onContentVisible) {
                onContentVisible();
              }
              return true;
            });
          } else if (unloadOffset > 0) {
            // Unload content when far from viewport (for large lists)
            const rect = entry.boundingClientRect;
            const viewportHeight = window.innerHeight;
            const distanceFromViewport = Math.min(
              Math.abs(rect.top - viewportHeight),
              Math.abs(rect.bottom)
            );

            if (distanceFromViewport > unloadOffset) {
              setIsVisible(false);
            }
          }
        });
      },
      {
        rootMargin: `${offset}px`, // Load when [offset]px away from viewport
        threshold: 0,
      }
    );

    observer.observe(placeholder);

    // Cleanup - disconnect observer to fully release resources
    // (important with many instances mounting/unmounting during fast scrolling)
    return () => {
      observer.disconnect();
    };
  }, [offset, unloadOffset, onContentVisible]);

  // Render content or placeholder
  return (
    <div
      ref={placeholderRef}
      style={{
        height: typeof height === 'number' ? `${height}px` : height,
        width: '100%',
      }}
    >
      {isVisible ? children : null}
    </div>
  );
};


LazyLoad.propTypes = {
  children: PropTypes.node.isRequired,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  offset: PropTypes.number,
  unloadOffset: PropTypes.number,
  onContentVisible: PropTypes.func,
};

LazyLoad.defaultProps = {
  height: 100,
  offset: 0,
  unloadOffset: 0,
  onContentVisible: null,
};

export default LazyLoad;